import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { endsWithAny } from 'src/app/core/util/string-end-checker';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { inProcessingRecord } from '../../data/in-processing-record';
import { SidebarType } from '../../enums/sidebar-type';
import { RecognizedInvoiceService } from '../../services/recognized-invoice.service';
import { take } from 'rxjs/operators';
import { UserService } from 'src/app/data/service/user.service';
import { InvoiceUnReconciledAdd } from '../../models/invoice/invoice-short';

@Component({
  selector: 'app-upload-sidebar',
  templateUrl: './upload-sidebar.component.html',
  styleUrls: ['./upload-sidebar.component.scss']
})
export class UploadSidebarComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() recordId: string = '';
  @Input() isBankTransMode: boolean = false;
  /** Files dropped somewhere else (the Inbox, the dashboard's Expenses card) to start with. */
  @Input() initialFiles: File[] = null;
  @Output() uploadedFile = new EventEmitter<object>();
  @Output() isClosed = new EventEmitter<SidebarType>();

  /** The manual coding dialog is open over the panel. */
  showManualCoding: boolean = false;

  isLoaded: boolean = false;
  isPdf: boolean = false;
  loading: boolean = false;
  tempFile: string;
  /** The documents chosen so far. Each is submitted as its own upload. */
  files: File[] = [];
  /** Which of them the preview shows. */
  selectedIndex: number = 0;
  validFileTypes: string[] = ["application/pdf", 'image/png', 'image/jpg', 'image/jpeg'];
  organizationEmail: string = null;
  showToolTipDelay: number = 400;
  isDisabled: boolean = true;
  mailboxData: { companyEmail?: string; personalEmail?: string } = {};
  roleId: string = null;
  /** An image's zoom, in percent of the preview's width (a PDF's is the viewer's own). */
  imageScale: number = 100;

  /** The document being previewed. */
  get file(): File {
    return this.files[this.selectedIndex] || null;
  }

  /** Several documents at once, only when uploading new expenses: attaching to a record or a
   *  bank transaction takes exactly one. */
  get allowMultiple(): boolean {
    return !this.isBankTransMode && !this.recordId;
  }

  /** "240 KB" / "1.3 MB", under the file name. */
  fileSizeLabel(file: File): string {
    const bytes = file?.size || 0;
    if (bytes < 1024 * 1024) { return `${Math.max(1, Math.round(bytes / 1024))} KB`; }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  constructor(
    private _toasterMessageService: ToasterMessageService,
    private _invoiceService: InvoiceService,
    private _recognizedInvoiceService: RecognizedInvoiceService,
    private _organizationService: OrganizationService,
    private _userService: UserService
  ) {
    super();
  }

  /** Files dropped while the panel was already open join the ones there (or replace the
   *  one there, when only one is allowed). */
  ngOnChanges(changes: SimpleChanges) {
    const change = changes['initialFiles'];
    if (change && !change.firstChange && change.currentValue?.length) {
      this.addFiles(change.currentValue);
    }
  }

  ngOnInit() {
    if (this.initialFiles?.length) {
      this.addFiles(this.initialFiles);
    }
    this._userService.getUserGetByIdServer().subscribe(response => {
      if (response && response.data) {
        this.roleId = response.data.role.toString();
      }
    });
    this.getOrganizationEmail();
  }

  // get roleId(): number {
  //   return +(localStorage.getItem('roleId') || sessionStorage.getItem('roleId') || sessionStorage.getItem('roleID') || 0);
  // }

  getOrganizationEmail() {
    const orgId = this._organizationService.selectedOrganization;
    const userId = localStorage.getItem("Id") || sessionStorage.Id || sessionStorage.UserId;
    this._organizationService.getOrganizationEmail(orgId, userId)
      .pipe(this.untilThis)
      .subscribe(
        (response) => {
          // Handle both new and old API response formats
          if ('companyEmail' in response || 'personalEmail' in response) {
            this.mailboxData = response;
          } else if ('email' in response) {
            this.mailboxData = { companyEmail: response.email, personalEmail: null };
          } else {
            this.mailboxData = {};
          }
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      )
  }

  dropped(uploadedFiles: NgxFileDropEntry[]) {
    const entries = uploadedFiles
      .filter(f => f.fileEntry.isFile)
      .map(f => f.fileEntry as FileSystemFileEntry);
    const toRead = this.allowMultiple ? entries : entries.slice(0, 1);
    // Each entry hands its file over asynchronously, so each is added as it arrives.
    toRead.forEach(entry => entry.file((file: File) => this.addFiles([file])));
  }

  added(event: Event) {
    const input = event.target as HTMLInputElement;
    const chosen = Array.from(input.files as FileList);
    // Cleared so that choosing the same file again, after removing it, still fires (change).
    input.value = '';
    this.addFiles(chosen);
  }

  /** Adds the valid documents among `files` and previews the first of them. Only one is kept
   *  when only one is allowed; the same file chosen twice is kept once. */
  addFiles(files: File[]) {
    const valid = files.filter(f => endsWithAny(this.validFileTypes, f.type));
    if (valid.length < files.length) {
      const skipped = files.length - valid.length;
      this._toasterMessageService.displayToasterState(ToasterTypes.Warning, 'Warning',
        files.length === 1 ? 'Invalid file type!' : `${skipped} file${skipped === 1 ? ' was' : 's were'} skipped: only PDF, PNG and JPG are supported.`);
    }
    if (!valid.length) {
      return;
    }
    if (!this.allowMultiple) {
      this.files = [valid[0]];
      this.selectFile(0);
      return;
    }
    const isSame = (a: File, b: File) => a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
    const fresh = valid.filter(f => !this.files.some(existing => isSame(existing, f)));
    if (!fresh.length) {
      return;
    }
    const firstNew = this.files.length;
    this.files = [...this.files, ...fresh];
    this.selectFile(firstNew);
  }

  selectFile(index: number) {
    if (!this.files[index]) {
      return;
    }
    this.selectedIndex = index;
    this.imageScale = 100;
    this._setPreview(this.files[index]);
  }

  /** Drops one document; the panel goes back to the drop zone once none are left. */
  removeFile(index: number) {
    this.files = this.files.filter((_, i) => i !== index);
    if (!this.files.length) {
      this.deleteFile();
      return;
    }
    this.selectFile(Math.min(index, this.files.length - 1));
  }

  sendFile(file: File) {
    this._invoiceService.uploadInvoice({ organisationId: this._organizationService.selectedOrganization, invoice: file, recordId: this.recordId, isBankTransMode: this.isBankTransMode });
  }

  deleteFile() {
    this.tempFile = '';
    this.files = [];
    this.selectedIndex = 0;
    this.isLoaded = false;
    this.loading = false;
    this.isDisabled = true;
  }

  save() {
    if (!this.file) {
      return;
    }
    if (this.isBankTransMode) {
      this.uploadedFile.emit({ name: this.file.name, file: this.file });
      //this.uploadedFile.emit({file:this.file});
      this.isClosed.emit(SidebarType.upload);
      this.deleteFile();
    }
    else {

      this.loading = true;

      if (this.allowMultiple) {
        this._invoiceService.uploadInvoices(this.files, this._organizationService.selectedOrganization);
      } else {
        this.sendFile(this.file);
      }
      // if (this.isBankTransMode) {
      //   this._recognizedInvoiceService.invoiceState$.pipe(take(1))
      //     .subscribe(
      //       (invoice: any) => {
      //         this.uploadedFile.emit({name:this.file.name,id:invoice.id});
      //         this.isClosed.emit(SidebarType.upload);
      //         this.deleteFile();
      //       },
      //       (e: Error) => {
      //         this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', e.message);
      //       }
      //     );
      // }
      // else{
      this.isClosed.emit(SidebarType.upload);
      this.deleteFile();
      // }
    }
  }

  cancel() {
    this.isClosed.emit(SidebarType.upload);
  }

  /** Not when attaching a document to a bank transaction: that needs a file. */
  get canCodeManually(): boolean {
    return !this.isBankTransMode && !this.recordId;
  }

  openManualCoding() {
    this.showManualCoding = true;
  }

  /** The dialog closed: with the expense to submit, or with nothing (cancelled). The
   *  service reports progress and the result itself, so the panel can go at once. */
  onManualCodingClosed(result: InvoiceUnReconciledAdd | null) {
    this.showManualCoding = false;
    if (result) {
      this._invoiceService.uploadInvoiceManually(result);
      this.isClosed.emit(SidebarType.upload);
    }
  }

  /** One step in (+1) or out (-1) on an image, between 50% and 400%. */
  zoomImage(direction: number) {
    this.imageScale = Math.min(400, Math.max(50, this.imageScale + direction * 25));
  }

  private _setPreview(file: File) {
    const reader = new FileReader();

    reader.onloadend = () => {
      // Another document was selected while this one was being read.
      if (this.file !== file) { return; }
      this.tempFile = reader?.result as string;
      this.isPdf = (file.type === "application/pdf") ? true : false;
      this.isLoaded = true;
      this.isDisabled = false;
    }

    reader.readAsDataURL(file);
  }

  copyToClipboard(email: string) {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(email).then(() => {
        this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Copied!', 'Email address copied to clipboard.');
      }, () => {
        this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', 'Failed to copy email address.');
      });
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = email;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Copied!', 'Email address copied to clipboard.');
      } catch {
        this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', 'Failed to copy email address.');
      }
      document.body.removeChild(textarea);
    }
  }
}
